import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MiniProductAnimation } from "./MiniProductAnimations";
import { useLanguage } from "@/hooks/useLanguage";

const products = [
  {
    key: "atlas" as const,
    href: "/tjanster/atlas",
  },
  {
    key: "valid" as const,
    href: "/tjanster/valid",
  },
  {
    key: "adresstwatt" as const,
    href: "/tjanster/adresstwatt",
  },
];

export function ProductsSection() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <section className="py-12 lg:py-32 bg-secondary/30 min-h-[400px]">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-8 lg:mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t("products.title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-body">
            {t("products.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
            >
              <Link
                to={getLocalizedPath(product.href)}
                className="group block rounded-2xl overflow-hidden border border-border bg-card hover:shadow-xl hover:border-primary/20 transition-all duration-400 h-full"
              >
                {/* Animation preview */}
                <div className="bg-primary/[0.03] h-48 overflow-hidden flex items-center justify-center p-4 border-b border-border group-hover:bg-primary/[0.06] transition-colors duration-300">
                  <MiniProductAnimation productKey={product.key} />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold font-subheading text-foreground mb-2">
                    {t(`products.${product.key}.title`)}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed text-sm mb-4">
                    {t(`products.${product.key}.description`)}
                  </p>
                  <span className="inline-flex items-center text-sm font-semibold text-coral gap-1.5 group-hover:gap-2.5 transition-all">
                    {t(`products.${product.key}.cta`)}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
